import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';

import { debounceTime, distinctUntilChanged, Observable, Subscription, filter } from 'rxjs';
import { Region } from 'src/app/model/region.model';
import { HelperService } from 'src/app/service/helper.service';
import { LoginTokenService } from 'src/app/service/login-token.service';
import { RegionEntityService } from '../../service/region-entity.service';
import { ErrorStateMatcher } from '@angular/material/core';

@Component({
    selector: 'region-dropdown',
    templateUrl: './region-dropdown.component.html',
    styleUrls: ['./region-dropdown.component.scss']
})
export class RegionDropdownComponent implements OnInit {
    @Input() selectRegionIds: number[] = [];
    @Input() RegionCode: string;
    @Input() isMultiple: boolean = true;
    @Input() allowAllRegions: boolean = false;
    @Input() disableAutoComplete: boolean = false;
    @Input() ErrorStateMatcher: any = null;
    @Input('reset') reset: boolean;
    @Input() required: boolean = false;
    @Input('entitlementData') entitlementData: boolean = false;
    @Input('disabled') disabled: boolean;
    @Input('selectUserRegion') selectUserRegion: boolean = true;
    @Input('customLabel') customLabel: string;

    @Output() eventEmitterRegioncode = new EventEmitter<Region[]>();

    @ViewChild('filterRef') filterRef: ElementRef;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSelect) matSelect: MatSelect;

    fieldLabel: string;

    regionForm: UntypedFormGroup;
    regionIds: number[] = [];
    selectedRegionIds: number[] = [];

    selectedRegionName: string | undefined;
    filteredRegions: Region[] = [];
    filteredRegionsShow: Region[] = [];

    regions: Region[] = [];
    allRegions: Region[] = [];

    userRegion: Region = new Region();
    userRegionLoaded: boolean = false;
    hasAdminRole: boolean = false;

    loaded$: Observable<boolean>;
    isLoading: boolean = false;
    changed: boolean = true;

    subs: Subscription[] = [];

    pageIndex: number = 0;
    pageSize: number = 100;

    matcher = this;

    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const isSubmitted = form && form.submitted;
        return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
    }

    constructor(
        private fb: UntypedFormBuilder,
        private regionService: RegionEntityService,
        private helperService: HelperService,
        private loginTokenService: LoginTokenService
    ) {
        this.regionForm = this.fb.group({
            regionSelect: [''], //{value:'', disabled: true}
            filter: ['']
        });

        this.loaded$ = regionService.loaded$;
        let loadedSub = this.loaded$?.subscribe((loaded) => {
            if (!loaded || this.disabled) {
                this.regionSelect.disable();
            } else {
                this.regionSelect.enable();
            }
        });
        this.subs.push(loadedSub);
    }

    subscribers() {
        let regionEntitiesSub = this.regionService.entities$.subscribe((regions: Region[]) => {
            this.allRegions = regions;
            this.regionIds = this.allRegions.length > 0 ? this.allRegions.map((region) => region.ID!) : [];
            this.changed = true;
            this.loadData(true);
        });
        this.subs.push(regionEntitiesSub);

        let lstUserProfileSub = this.helperService.lstUserProfileRegion$.subscribe((userRegion: Region[]) => {
            this.userRegion = userRegion[0];
        });
        this.subs.push(lstUserProfileSub);

        let filterInputSub = this.filterInput!.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value: string) => {
            if (value.length === 0) {
                this.filteredRegions = this.regions;
            } else {
                this.filteredRegions = this.regions.filter(
                    (region) => region.CODE!.toLowerCase().includes(value.toLowerCase()) || region.NAME!.toLowerCase().includes(value.toLowerCase())
                );
                this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });
            }
        });
        this.subs.push(filterInputSub);
    }

    emitSelect(regionIds: number[]) {
        let output: Region[] = [];
        this.selectedRegionIds = Array.from(new Set(this.selectedRegionIds.concat(regionIds)));
        this.selectedRegionName = '';

        if (regionIds.length > 0) {
            this.regions.forEach((filteredRegion) => {
                regionIds.forEach((regionId) => {
                    if (regionId == filteredRegion.ID) {
                        output.push(filteredRegion);
                        this.selectedRegionName += this.selectedRegionName!.length > 0 ? ', ' + filteredRegion.CODE : filteredRegion.CODE;
                    }
                });
            });
        }

        this.helperService.setSelectedRegionObject(output);
        this.filterInput?.patchValue('');
        output[0] ? this.goToPage(output[0].ID!) : this.goToPage(0);
        //Emits the code to the subscriber.
        this.eventEmitterRegioncode.emit(output);
    }

    ngOnInit(): void {
        if (this.customLabel) {
            this.fieldLabel = this.customLabel;
        } else {
            this.fieldLabel = 'Region';
        }

        let initLoadedSub = this.loaded$?.subscribe((loaded) => {
            if (loaded) {
                // Initialize user, disable field if not an admin/state
                this.initUser();

                setTimeout(() => {
                    if (this.selectRegionIds.length > 0) {
                        this.select(this.selectRegionIds);
                        this.emitSelect(this.selectRegionIds);
                    } else if (this.userRegion && !this.disableAutoComplete) {
                        if (this.selectUserRegion) {
                            this.select([this.userRegion.ID]);
                            this.emitSelect([this.userRegion.ID]);
                        }
                    } else {
                        this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });
                    }

                    //this.selectRegionIds = [];
                    initLoadedSub.unsubscribe();
                }, 500);
            }
        });
        this.subs.push(initLoadedSub);

        this.subscribers();
    }

    ngOnDestroy() {
        this.subs.forEach((sub) => {
            sub.unsubscribe();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.reset) {
            if (this.userRegion && this.selectUserRegion) {
                this.select([this.userRegion.ID]);
                this.emitSelect([this.userRegion.ID]);
            } else {
                this.deSelectAll();
            }
        }
        if ((this.hasAdminRole || this.allowAllRegions) && !this.disabled) {
            this.regionSelect.enable();
        } else {
            this.regionSelect.disable();
        }

        if (changes['selectRegionIds']) {
            if (this.selectRegionIds.length > 0) {
                this.select(this.selectRegionIds);

                this.emitSelect(this.selectRegionIds);
            } else {
                this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });
            }

            //this.selectRegionIds = [];
        }
    }

    initUser() {
        let user = this.loginTokenService.getUser();

        let adminUserIds = [1, 9]; // System Administrator (1), State Administrator (9)

        this.hasAdminRole = user.USER_ROLES?.some((ur) => adminUserIds.includes(ur.ROLE?.ID ? ur.ROLE.ID : 0))!;

        if ((this.hasAdminRole || this.allowAllRegions) && !this.disabled) {
            this.regionSelect.enable();
        } else {
            this.regionSelect.disable();
        }
    }

    loadData(open: any) {
        if (open) {
            this.filterRef ? this.filterRef.nativeElement.focus() : null;
            if (!this.changed) return;

            this.regions = [];
            this.isLoading = true;

            this.regionSelect.disable();

            this.changed = false;

            this.filterInput!.patchValue('');

            let workingSchoolDistricts = this.allRegions;

            this.regions = Array.from(new Set(workingSchoolDistricts));

            this.regions.sort((a, b) => a.CODE!.localeCompare(b.CODE!));
            this.filteredRegions = this.regions;

            if (this.regions.length === 1 && !this.disableAutoComplete) {
                this.regionSelect.patchValue([]);
                this.selectAll();
                this.emitSelect(this.selectedRegionIds);
            } else {
                this.select(this.selectedRegionIds);
                this.emitSelect(this.selectedRegionIds);
            }

            if (!this.disabled) {
                this.regionSelect.enable();
            }

            this.isLoading = false;
        } else {
            this.select(this.selectedRegionIds);
            this.emitSelect(this.selectedRegionIds);
        }
    }

    removeDuplicateIDs(arr: Array<any>): Array<any> {
        arr = arr.filter((value, index, self) => index === self.findIndex((t) => t.place === value.place && t.ID === value.ID));
        return arr;
    }

    select(selection: any) {
        //console.log('now calling region select');
        // Get current page
        this.filteredRegions = this.regions;

        if (this.isMultiple) {
            // Remove all selections in current page
            let currentPageIds = this.filteredRegionsShow.map((show) => show.ID);
            this.selectedRegionIds = this.selectedRegionIds.filter((selectedRegionId) => !currentPageIds.includes(selectedRegionId));

            // Add current selection to total array
            this.selectedRegionIds = Array.from(new Set(this.selectedRegionIds.concat(selection)));

            // Remove all IDs that are not in available regions
            let filteredRegions = this.filteredRegions.map((filteredRegion) => filteredRegion.ID);
            this.selectedRegionIds = this.selectedRegionIds.filter((selectedRegionId) => filteredRegions.includes(selectedRegionId));

            this.regionSelect.patchValue(this.selectedRegionIds);
        } else {
            if (Array.isArray(selection)) {
                //console.log('was array');
                this.regionSelect.patchValue(selection[0]);
                this.selectedRegionIds = selection;
            } else {
                //console.log('was not array');
                this.regionSelect.patchValue(selection);
                this.selectedRegionIds = [selection];
            }

            this.goToPage(this.regionSelect.value);
        }
    }

    selectAll() {
        let selected = this.regionSelect.value; // Currently selected
        if (this.isMultiple) {
            selected = Array.isArray(selected) ? selected : [];
            this.selectedRegionIds = Array.from(new Set(selected.concat(this.filteredRegions.map((region) => region.ID!)))); // Select all filtered (if any filter)
            this.regionSelect.patchValue(this.selectedRegionIds);
        } else {
            this.selectedRegionIds = this.filteredRegions.map((region) => region.ID!);
            this.regionSelect.patchValue(this.selectedRegionIds[0]);
        }
    }

    deSelectAll() {
        this.reset = false;
        this.regionSelect.patchValue([]);
        this.selectedRegionIds = [];
        this.filterInput?.patchValue('');
        this.emitSelect([]);
    }

    close() {
        this.matSelect.close();
    }

    get regionSelect(): any {
        return this.regionForm.get('regionSelect');
    }
    get filterInput(): AbstractControl | null {
        return this.regionForm.get('filter');
    }

    onPageChange($event: any) {
        $event.pageIndex = $event.pageIndex < 0 ? 0 : $event.pageIndex;

        this.pageIndex = $event.pageIndex;
        this.pageSize = $event.pageSize;

        this.filteredRegionsShow = this.filteredRegions.slice($event.pageIndex * $event.pageSize, $event.pageIndex * $event.pageSize + $event.pageSize);
    }

    goToPage(id: number) {
        let index = this.filteredRegions.findIndex((filteredRegion) => filteredRegion.ID === id);
        let pageIndex = Math.floor(index / this.pageSize);

        if (this.paginator) {
            this.paginator.pageIndex = pageIndex;
        }
        this.onPageChange({ pageIndex: pageIndex, pageSize: this.pageSize });
    }
}
